import { useMemo, useCallback } from 'react';
import { useUnmount } from 'react-use';
import { area, curveMonotoneX, line } from 'd3-shape';
import { select, selectAll } from 'd3-selection';
import { scaleLinear, scaleTime } from 'd3-scale';
import { differenceInMinutes } from 'date-fns';

import { MoveTideLabel, type TideLocation, type TidePoint } from 'types/tide';
import midnightOfDate from 'utils/midnightOfDate';
import { formatTimestampInTimezone } from 'utils/time';
import { isFinite } from 'lodash';

const HEIGHT_LABEL_FILL = '#424242';
const TIME_LABEL_FILL = '#171717';
const GRAPH_LINE_FILL = '#171717';
// used to offset label 10px above point on curve
const TIDE_HEIGHT_LABEL_OFFSET_TOP = 10;
const TIDE_HEIGHT_LABEL_TOP_PADDING = 22;

const TIDES_TIMESTAMP_DIFFERENCE = 10800; // 3 hours = 10800

export const getClosestDataPoints = (dataPointIndexes: Array<number>, dataPointIndex: number) => {
  let previous: number | undefined;
  let next: number | undefined;
  const sortedDataPointIndexes = dataPointIndexes.sort((a, b) => a - b);
  sortedDataPointIndexes.some((dataPoint) => {
    if (dataPoint > dataPointIndex && next === undefined) {
      next = dataPoint;
    }
    return next;
  });
  sortedDataPointIndexes.reverse().some((dataPoint) => {
    if (dataPoint < dataPointIndex && previous === undefined) {
      previous = dataPoint;
    }
    return previous;
  });
  return {
    previous,
    next,
  };
};

export const shouldIncludeDataPointLabel = (
  tideData: Array<any>,
  indexesToKeep: Array<number>,
  indexToTest: number,
) => {
  const closestDataPoints = getClosestDataPoints(indexesToKeep, indexToTest);

  if (closestDataPoints.previous !== undefined) {
    if (
      tideData[indexToTest].timestamp - tideData[closestDataPoints.previous].timestamp <
      TIDES_TIMESTAMP_DIFFERENCE
    ) {
      return false;
    }
  }

  if (closestDataPoints.next !== undefined) {
    if (
      tideData[closestDataPoints.next].timestamp - tideData[indexToTest].timestamp <
      TIDES_TIMESTAMP_DIFFERENCE
    ) {
      return false;
    }
  }

  return true;
};

export const filterTideData = (tideData: Array<any>) => {
  const indexesToKeep: number[] = [];
  let currentDay: string | null = null;
  let currentLow: number | null = null;
  let lowIndexToKeep: number | null = null;
  let currentHigh: number | null = null;
  let highIndexToKeep: number | null = null;

  // Get the max high and low for each day
  tideData.forEach((dataPoint: any, index: number) => {
    const day: string = formatTimestampInTimezone(dataPoint.timestamp, dataPoint.utcOffset, 'd');

    if (currentDay !== day) {
      if (lowIndexToKeep !== null && !indexesToKeep.includes(lowIndexToKeep)) {
        indexesToKeep.push(lowIndexToKeep);
      }
      if (highIndexToKeep !== null && !indexesToKeep.includes(highIndexToKeep)) {
        indexesToKeep.push(highIndexToKeep);
      }
      currentDay = day;
      currentLow = null;
      lowIndexToKeep = null;
    }

    if (dataPoint.type === 'LOW') {
      if (currentLow === null) {
        currentLow = dataPoint.height;
        lowIndexToKeep = index;
      } else if (currentLow > dataPoint.height) {
        currentLow = dataPoint.height;
        lowIndexToKeep = index;
      }
    }

    if (dataPoint.type === 'HIGH') {
      if (currentHigh === null) {
        currentHigh = dataPoint.height;
        highIndexToKeep = index;
      } else if (currentHigh < dataPoint.height) {
        currentHigh = dataPoint.height;
        highIndexToKeep = index;
      }
    }
  });

  // Add any dangling indexes that were not added due to no day change
  if (lowIndexToKeep !== null && !indexesToKeep.includes(lowIndexToKeep)) {
    indexesToKeep.push(lowIndexToKeep);
  }
  if (highIndexToKeep !== null && !indexesToKeep.includes(highIndexToKeep)) {
    indexesToKeep.push(highIndexToKeep);
  }

  const returnData: Array<any> = [];

  // Add all high and low data points and determine if they should display a label on the graph
  tideData.forEach((dataPoint: any, index: number) => {
    if (indexesToKeep.includes(index)) {
      returnData.push({ ...dataPoint, showLabel: true });
    } else if (dataPoint.type === 'LOW' || dataPoint.type === 'HIGH') {
      const showLabel = shouldIncludeDataPointLabel(tideData, indexesToKeep, index);
      returnData.push({ ...dataPoint, showLabel });
      if (showLabel) {
        indexesToKeep.push(index);
      }
    }
  });

  return returnData;
};

interface Props {
  data: Array<TidePoint>;
  dateFormat?: 'MDY' | 'DMY';
  dayIndex: number;
  endDate: number;
  graphHeight?: number;
  graphWidth?: number;
  moveTideLabel: MoveTideLabel;
  startDate: number;
  tideLocation: TideLocation;
  units: string;
}

const useSetupTideChart = ({
  data,
  dayIndex,
  endDate,
  graphHeight,
  graphWidth,
  moveTideLabel,
  startDate,
  tideLocation,
  units,
}: Props) => {
  const tideData = useMemo(
    () =>
      data.map((dataPoint: TidePoint) => ({
        ...dataPoint,
        dateTime: dataPoint ? new Date(dataPoint.timestamp * 1000) : null,
      })),
    [data],
  );

  const setupTideChart = useCallback(() => {
    if (!!graphHeight && !!graphWidth && isFinite(graphHeight) && isFinite(graphWidth)) {
      select(`.tide-chart-svg-day-${dayIndex}`).remove();
      selectAll(`.tide-chart-svg-${dayIndex}-high-low`).remove();
      const CHART_HEIGHT = graphHeight;
      const CHART_WIDTH = graphWidth;
      const dayHasData = (timestamp: number, utcOffset: number) => {
        if (!timestamp) return false;

        const dayStart = midnightOfDate(utcOffset, timestamp);
        const dayEnd = dayStart + 86400;

        return !!data.find((d) => d?.timestamp >= dayStart && d?.timestamp <= dayEnd);
      };

      const maxYDomain = tideLocation?.max;
      const minYDomain = tideLocation?.min;

      const xAxis = scaleTime().range([-1, CHART_WIDTH]).domain([startDate, endDate]);
      const yAxis = scaleLinear()
        .range([CHART_HEIGHT, TIDE_HEIGHT_LABEL_OFFSET_TOP + TIDE_HEIGHT_LABEL_TOP_PADDING])
        .domain([minYDomain, maxYDomain]);

      const xAxisAlignment = (dateTime: Date | number) => {
        const xAxisDateAlignment = xAxis(dateTime);
        if (
          (moveTideLabel === MoveTideLabel.RIGHT || moveTideLabel === MoveTideLabel.BOTH) &&
          differenceInMinutes(dateTime, startDate) < 50
        ) {
          return xAxisDateAlignment + 18;
        }
        if (
          (moveTideLabel === MoveTideLabel.LEFT || moveTideLabel === MoveTideLabel.BOTH) &&
          differenceInMinutes(dateTime, endDate) > -50
        ) {
          return xAxisDateAlignment - 18;
        }
        return xAxisDateAlignment;
      };

      const tideLine = line()
        .defined((d: any) => dayHasData(d.timestamp, d.utcOffset))
        .x((d: any) => xAxis(d.dateTime))
        .y((d: any) => yAxis(d.height))
        .curve(curveMonotoneX);

      const tideArea = area()
        .defined((d: any) => dayHasData(d.timestamp, d.utcOffset))
        .x((d: any) => xAxis(d.dateTime))
        .y1((d: any) => yAxis(d.height))
        .y0(CHART_HEIGHT)
        .curve(curveMonotoneX);

      const canvas = select(`#tide-chart-day-${dayIndex}`);
      const svg = canvas
        .append('svg')
        .attr('class', `tide-chart-svg-day-${dayIndex}`)
        .attr('position', 'relative')
        .attr('width', '100%')
        .attr('height', CHART_HEIGHT);

      svg
        .append('path')
        .datum(tideData)
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('class', `tide-chart-svg-${dayIndex}-fill`)
        .attr('d', tideArea(tideData as any))
        .attr('fill', 'rgba(185, 211, 223, 0.4)');

      svg
        .append('path')
        .datum(tideData)
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('class', `tide-chart-svg-${dayIndex}-fill`)
        .attr('d', tideLine(tideData as any))
        .attr('stroke', GRAPH_LINE_FILL)
        .attr('stroke-width', '1.5px')
        .attr('fill', 'none')
        .attr('shape-rendering', 'geometricPrecision');

      const filteredData = filterTideData(tideData);
      filteredData
        .filter((dataPoint: any) => dataPoint?.type !== 'NORMAL')
        .forEach(({ dateTime, height, timestamp, utcOffset, showLabel }) => {
          const highLow = svg.append('g').attr('class', `tide-chart-svg-${dayIndex}-high-low`);
          const roundedHeight = height?.toFixed(1);
          const time = formatTimestampInTimezone(timestamp, utcOffset, 'h:mmaaa');
          if (showLabel) {
            highLow
              .append('text')
              .attr(
                'class',
                `tide-chart-svg-high-low-label tide-chart-svg-${dayIndex}-high-low-label`,
              )
              .attr('data-testid', 'tide-chart-svg-high-low-label')
              .attr('text-anchor', 'middle')
              .attr('font-size', '10px')
              .attr('font-weight', 600)
              .attr('font-family', 'Linear Sans')
              .attr('line-height', '16px')
              .attr('fill', HEIGHT_LABEL_FILL)
              .attr('x', xAxisAlignment(dateTime || 0))
              .attr('y', yAxis(height) - TIDE_HEIGHT_LABEL_OFFSET_TOP)
              .text(`${roundedHeight}${units?.toLowerCase()}`);

            highLow
              .append('text')
              .attr('class', `tide-chart-svg-time-label tide-chart-svg-${dayIndex}-time-label`)
              .attr('data-testid', 'tide-chart-svg-time-label')
              .attr('text-anchor', 'middle')
              .attr('font-size', '10px')
              .attr('font-weight', 700)
              .attr('font-family', 'Linear Sans')
              .attr('line-height', '16px')
              .attr('fill', TIME_LABEL_FILL)
              .attr('z-index', '999')
              .attr('x', xAxisAlignment(dateTime || 0))
              .attr('y', yAxis(height) - TIDE_HEIGHT_LABEL_OFFSET_TOP - 12)
              .text(time);
          }

          highLow
            .append('line')
            .attr('class', `tide-chart-svg-${dayIndex}-high-low-line`)
            .attr('x1', xAxis(dateTime || 0))
            .attr('x2', xAxis(dateTime || 0))
            .attr('y1', yAxis(height) + 1)
            .attr('y2', '100%')
            .style('stroke-width', 1)
            .style('stroke', GRAPH_LINE_FILL)
            .style('opacity', 0.4)
            .style('fill', 'none');
        });
    }
  }, [
    data,
    dayIndex,
    endDate,
    graphHeight,
    graphWidth,
    moveTideLabel,
    startDate,
    tideData,
    tideLocation?.max,
    tideLocation?.min,
    units,
  ]);

  useUnmount(() => {
    select(`.tide-chart-svg-day-${dayIndex}`).remove();
    selectAll(`.tide-chart-svg-${dayIndex}-high-low`).remove();
  });

  return { setupTideChart };
};

export default useSetupTideChart;
